/**
 * Login form with username/password authentication and master key option (dev mode)
 * Enhanced with dynamic branding from config
 */

import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Modal } from '../UI/Modal';
import { useAuthContext } from './AuthProvider';
import { RegisterForm } from './RegisterForm';
import { ForgotPassword } from './ForgotPassword';
import { useAuthStore } from '../../stores/auth';
import { getAPIBaseUrl } from '../../api/client';
import { sendHelpRequestPublic } from '../../api/feedback';
import { useToastStore } from '../../stores/toast';
import { Key, Shield, Mail, Lock, Eye, EyeOff, Code, AlertCircle } from 'lucide-react';
import { TraceKitConfig } from '../../types/config';

const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

// Helper function to convert hex color to RGB for opacity
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 'rgb(59, 130, 246)'; // Default blue
  return `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`;
}

// Helper function to generate gradient colors from primary and secondary
function generateGradientColors(primary: string, secondary: string): {
  from: string;
  via: string;
  to: string;
  fromRgb: string;
  viaRgb: string;
  toRgb: string;
} {
  const primaryRgb = hexToRgb(primary);
  const secondaryRgb = hexToRgb(secondary);
  
  // Create intermediate color by mixing
  const primaryMatch = primaryRgb.match(/\d+/g);
  const secondaryMatch = secondaryRgb.match(/\d+/g);
  
  if (primaryMatch && secondaryMatch) {
    const viaR = Math.round((parseInt(primaryMatch[0]) + parseInt(secondaryMatch[0])) / 2);
    const viaG = Math.round((parseInt(primaryMatch[1]) + parseInt(secondaryMatch[1])) / 2);
    const viaB = Math.round((parseInt(primaryMatch[2]) + parseInt(secondaryMatch[2])) / 2);
    const viaRgb = `rgb(${viaR}, ${viaG}, ${viaB})`;
    
    return {
      from: primary,
      via: `rgb(${viaR}, ${viaG}, ${viaB})`,
      to: secondary,
      fromRgb: primaryRgb,
      viaRgb,
      toRgb: secondaryRgb,
    };
  }
  
  return {
    from: primary,
    via: primary,
    to: secondary,
    fromRgb: primaryRgb,
    viaRgb: primaryRgb,
    toRgb: secondaryRgb,
  };
}

interface LoginFormProps {
  config?: TraceKitConfig;
}

/** Logo + title + subtitle for login (desktop: left column; mobile: above form) */
function LoginBrandingBlock({
  align,
  branding,
  hasLogoImage,
  logoGradientStyle,
}: {
  align: 'left' | 'center';
  branding: {
    appName: string;
    logoUrl: string | null;
    logoAlt: string;
    platformTitle: string;
    platformSubtitle: string;
    poweredBy: string;
    primaryColor: string;
    isRfpAgent: boolean;
  };
  hasLogoImage: boolean;
  logoGradientStyle: React.CSSProperties;
}) {
  return (
    <header className="text-center" aria-label="Application branding">
      <div
        className="mx-auto mb-4 rounded-2xl shadow-xl flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24"
        style={logoGradientStyle}
      >
        {hasLogoImage ? (
          <img
            src={branding.logoUrl!}
            alt={branding.logoAlt}
            className="w-14 h-14 sm:w-16 sm:h-16 object-contain"
          />
        ) : (
          <Shield className="w-14 h-14 sm:w-16 sm:h-16 text-white" aria-hidden />
        )}
      </div>
      <h1
        className="text-2xl sm:text-3xl lg:text-3xl font-bold tracking-tight"
        style={{ color: branding.primaryColor }}
      >
        {branding.appName}
      </h1>
      <p className="mt-1.5 text-sm sm:text-base text-slate-600 font-medium">{branding.platformSubtitle}</p>
      {!branding.isRfpAgent && (
        <p className="mt-1 text-xs sm:text-sm text-slate-500">
          Powered by {branding.platformTitle?.includes('Keystone') ? 'Keystone AI' : branding.poweredBy}
        </p>
      )}
    </header>
  );
}

export function LoginForm({ config }: LoginFormProps = {}) {
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showMasterKey, setShowMasterKey] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [masterKey, setMasterKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpSubject, setHelpSubject] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [helpMessageTouched, setHelpMessageTouched] = useState(false);
  const [isSendingHelp, setIsSendingHelp] = useState(false);
  const [helpSuccessSent, setHelpSuccessSent] = useState(false);
  const { login, isValidating } = useAuthContext();
  const { addToast } = useToastStore();

  // Check for error messages in URL (from OAuth redirects)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    if (error) {
      addToast({
        type: 'error',
        message: decodeURIComponent(error),
      });
      // Clear error from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [addToast]);

  // Extract branding values with fallbacks (align with landing page when Keystone RFP Agent)
  const branding = useMemo(() => {
    const brandingConfig = config?.branding as any || {};
    const primaryColor = brandingConfig.primaryColor || '#1e40af';
    const secondaryColor = brandingConfig.secondaryColor || '#3b82f6';
    const isRfpAgent = brandingConfig.appName === 'Keystone RFP Agent' || brandingConfig.appName === 'GovEarner';

    return {
      appName: brandingConfig.appName || 'TraceKit',
      logoUrl: brandingConfig.logoUrl || brandingConfig.logo || null,
      logoAlt: brandingConfig.logoAlt || brandingConfig.appName || 'Logo',
      platformTitle: brandingConfig.platformTitle || brandingConfig.appName || 'TraceKit',
      platformSubtitle: brandingConfig.platformSubtitle || brandingConfig.companyName || 'Evidence-Driven Intelligence Platform',
      poweredBy: brandingConfig.platformTitle || brandingConfig.appName || 'TraceOS AI',
      primaryColor,
      secondaryColor,
      gradient: generateGradientColors(primaryColor, secondaryColor),
      isRfpAgent,
    };
  }, [config]);

  // Determine if we should show logo image or icon
  const hasLogoImage = branding.logoUrl && (branding.logoUrl.startsWith('http') || branding.logoUrl.startsWith('/'));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidating) return;
    
    // Master key login (dev mode only)
    if (showMasterKey && masterKey.trim()) {
      try {
        const apiBaseUrl = getAPIBaseUrl();
        const key = masterKey.trim();
        const testResponse = await fetch(`${apiBaseUrl}/users`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${key}`,
            'Content-Type': 'application/json',
          },
        });
        
        // Master key is valid if we get 200 (users found) or 404 (no users but endpoint exists)
        // 401 means invalid key
        if (testResponse.status === 200 || testResponse.status === 404) {
          const { setApiKey } = useAuthStore.getState();
          setApiKey(key);
          
          const { getAPIClient } = await import('../../api/client');
          const client = getAPIClient();
          client.setApiKey(key);
          
          // Try to fetch the real user by email
          try {
            const usersResponse = await client.get('/users', { 
              params: { active_only: true } 
            });
            const users = usersResponse.data?.users || [];
            const parsaUser = users.find((u: any) => u.email === 'parsa@alpyne.ai');
            
            if (parsaUser) {
              useAuthStore.getState().setUser({
                id: parsaUser.id,
                email: parsaUser.email,
                username: parsaUser.username,
                full_name: parsaUser.full_name,
                is_email_verified: parsaUser.is_email_verified,
              });
            } else {
              useAuthStore.getState().setUser({
                id: 'master-key-user',
                email: 'parsa@alpyne.ai',
                username: 'parsa',
                full_name: 'Parsa Riahi',
                is_email_verified: true,
              });
            }
          } catch (error) {
            console.warn('Could not fetch user by email, using placeholder:', error);
            useAuthStore.getState().setUser({
              id: 'master-key-user',
              email: 'parsa@alpyne.ai',
              username: 'parsa',
              full_name: 'Parsa Riahi',
              is_email_verified: true,
            });
          }
          
          window.location.reload();
        } else if (testResponse.status === 401) {
          throw new Error('Invalid master key. Use traceos-dev-master-key (must match MASTER_DEV_API_KEY in .env).');
        } else if (testResponse.status >= 500) {
          throw new Error(
            'API server error (database may be down). Start Docker Desktop, then run: docker start traceos-postgres traceos-redis'
          );
        } else {
          throw new Error(`Authentication failed: ${testResponse.status}`);
        }
      } catch (error: any) {
        alert(error.message || 'Failed to login with master key. Please check your key.');
      }
      return;
    }
    
    // Username/password login
    if (!username.trim() || !password.trim()) return;
    
    setLoginError(null);
    try {
      const response = await login({
        username: username.trim(),
        password: password.trim(),
        remember_me: rememberMe,
      });

      // Check if password reset is required - if so, login() already redirected
      // Don't redirect to home if password reset is needed
      if ((response as any)?.requires_password_reset) {
        // Login function already redirected to reset page
        return;
      }
      
      // Navigate to home via SPA (avoids full reload and redundant tenants/projects refetches)
      window.dispatchEvent(new CustomEvent('navigate', { detail: { path: '/' } }));
    } catch (error: any) {
      const raw = error?.response?.data?.detail ?? error?.message;
      const message = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw.map((r: any) => r?.msg ?? r?.loc?.join?.('. ') ?? String(r)).join('. ') : 'Invalid username or password. Please try again.';
      setLoginError(message || 'Invalid username or password. Please try again.');
    }
  };

  const handleHelpSubmit = async () => {
    setHelpMessageTouched(true);
    if (!helpMessage.trim()) return;
    setIsSendingHelp(true);
    try {
      const res = await sendHelpRequestPublic({
        subject: helpSubject.trim() || undefined,
        message: helpMessage.trim(),
      });
      console.log('res help request', res);
      if (res.success) {
        setHelpSuccessSent(true);
        // Show in-modal success, then after delay close modal and show toast so notification is visible
        setTimeout(() => {
          setShowHelpModal(false);
          setHelpSuccessSent(false);
          setHelpSubject('');
          setHelpMessage('');
          setHelpMessageTouched(false);
          addToast({
            type: 'success',
            title: 'Message sent',
            message: res.message || 'Your message has been sent to the administrators.',
            duration: 5000,
          });
        }, 1800);
      } else {
        addToast({ type: 'error', message: res.message || 'Failed to send message.' });
      }
    } catch (err: any) {
      addToast({ type: 'error', message: err?.message || 'Failed to send help request. Please try again.' });
    } finally {
      setIsSendingHelp(false);
    }
  };

  const canUseDOM = typeof window !== 'undefined' && typeof document !== 'undefined';
  const helpMessageError = helpMessageTouched && !helpMessage.trim();

  // Show forgot password form if toggled
  if (showForgotPassword) {
    // Check for token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    return (
      <ForgotPassword 
        config={config} 
        onBack={() => {
          setShowForgotPassword(false);
          // Clear token from URL
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }}
        initialToken={tokenParam || undefined}
      />
    );
  }

  // Show register form if toggled
  if (showRegister) {
    return <RegisterForm onSuccess={() => setShowRegister(false)} config={config} />;
  }

  // Background: match landing page (soft blue gradient) for a warmer, less depressing feel
  const backgroundGradientStyle = {
    background: 'linear-gradient(180deg, #eff6ff 0%, #f0f9ff 35%, #f8fafc 70%, #f1f5f9 100%)',
  };

  // Generate dynamic logo container gradient style (landing-style)
  const logoGradientStyle = {
    background: `linear-gradient(to bottom right, ${branding.primaryColor}, ${branding.secondaryColor})`,
    boxShadow: `0 10px 25px -5px ${branding.primaryColor}40, 0 8px 10px -6px ${branding.primaryColor}30`,
  };

  // Three cards below login – always the original (reverted)
  const snapshotItems = [
    { value: '100%', label: 'Evidence-Backed' },
    { value: 'AI', label: 'Powered Insights' },
    { value: 'Fast', label: 'Real-Time Query' },
  ];

  // Value props for left column (features only, no numbers or “why sign in”)
  const valueProps = branding.isRfpAgent
    ? [
        'Faster proposal turnaround.',
        'Search across your document library.',
        'Evidence-based proposals with citations.',
        'Discover and shortlist opportunities (Bids & Tenders and more).',
        'Generate drafts and track progress from draft to approved.',
      ]
    : [
        'Evidence-backed answers with citations.',
        'AI-powered search and insights.',
        'Real-time query across your knowledge base.',
      ];

  return (
    <>
    <div
      className="relative h-[100dvh] max-h-[100dvh] min-h-0 overflow-hidden flex flex-col px-3 py-3 sm:px-5 sm:py-4"
      style={backgroundGradientStyle}
    >
      {/* Subtle pattern for life (dots or grid) */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.4] -z-10"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgb(30 64 175 / 0.15) 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
      />
      <div className="pointer-events-none absolute top-0 right-0 w-[min(80vw,400px)] h-[min(80vw,400px)] rounded-full bg-blue-100/50 blur-3xl -z-10" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-[min(60vw,300px)] h-[min(60vw,300px)] rounded-full bg-indigo-100/40 blur-3xl -z-10" />

      <div className="flex flex-1 min-h-0 w-full max-w-5xl mx-auto flex-col lg:flex-row gap-4 lg:gap-8 lg:items-center justify-center">
        {/* Left: branding above feature list (desktop) */}
        <aside className="hidden lg:flex flex-col justify-center gap-4 min-h-0 w-full max-w-sm xl:max-w-md shrink-0">
          <LoginBrandingBlock
            align="left"
            branding={branding}
            hasLogoImage={!!hasLogoImage}
            logoGradientStyle={logoGradientStyle}
          />
          <div className="rounded-2xl border border-slate-200/60 bg-white/90 backdrop-blur-sm p-5 shadow-lg">
            <ul className="space-y-2.5 text-sm text-slate-600" aria-label="Platform features">
              {valueProps.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span
                    className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: branding.primaryColor }}
                    aria-hidden
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Right: mobile branding + login + stats */}
        <div className="flex flex-col min-h-0 flex-1 w-full max-w-md mx-auto lg:mx-0 gap-2 sm:gap-3">
          <div className="shrink-0 lg:hidden">
            <LoginBrandingBlock
              align="center"
              branding={branding}
              hasLogoImage={!!hasLogoImage}
              logoGradientStyle={logoGradientStyle}
            />
          </div>

        {/* Login card – landing-style card (scrolls internally only if viewport is too short) */}
        <div className="bg-white/95 backdrop-blur-sm shadow-xl rounded-2xl p-5 sm:p-6 lg:p-7 border border-slate-200/60 min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col">
          {/* Toggle between login methods */}
          {isDev && (
            <div className="mb-4 flex gap-2 p-1 bg-slate-100 rounded-lg">
              <button
                type="button"
                onClick={() => setShowMasterKey(false)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  !showMasterKey
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Key className="h-4 w-4 inline mr-2" />
                Username/Password
              </button>
              <button
                type="button"
                onClick={() => setShowMasterKey(true)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  showMasterKey
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Code className="h-4 w-4 inline mr-2" />
                Master Key
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            {loginError && !showMasterKey && (
              <div
                role="alert"
                className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800"
              >
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">Sign-in failed</p>
                  <p className="text-sm mt-1">{loginError}</p>
                  <p className="text-sm mt-2 opacity-90">Please check your username and password and try again.</p>
                </div>
              </div>
            )}
            {showMasterKey ? (
              /* Master Key Login (Dev Mode) */
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Master API Key
                </label>
                <Input
                  type="password"
                  value={masterKey}
                  onChange={(e) => setMasterKey(e.target.value)}
                  placeholder="Enter master API key"
                  leftIcon={<Code className="h-5 w-5 text-slate-400" />}
                  className="h-12 text-base"
                  autoComplete="off"
                  required
                />
                <p className="mt-2 text-xs text-slate-500">
                  Development mode only. Use your master API key for quick access.
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Username or Email
                  </label>
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username or email"
                    leftIcon={<Mail className="h-5 w-5 text-slate-400" />}
                    className="h-12 text-base"
                    autoComplete="username"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setLoginError(null); }}
                      placeholder="Enter your password"
                      leftIcon={<Lock className="h-5 w-5 text-slate-400" />}
                      rightIcon={
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5 text-slate-400" />
                          ) : (
                            <Eye className="h-5 w-5 text-slate-400" />
                          )}
                        </button>
                      }
                      className="h-12 text-base pr-12"
                      autoComplete="current-password"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {!showMasterKey && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 border-slate-300 rounded"
                    style={{
                      accentColor: branding.primaryColor,
                    }}
                  />
                  <span className="text-sm text-slate-600">Remember me</span>
                </label>
                <button
                  type="button"
                  className="text-sm font-medium transition-colors"
                  style={{ color: branding.primaryColor }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = branding.secondaryColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = branding.primaryColor;
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowForgotPassword(true);
                  }}
                >
                  Forgot password?
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={
                showMasterKey 
                  ? !masterKey.trim() || isValidating
                  : !username.trim() || !password.trim() || isValidating
              }
              className="w-full h-12 text-base font-semibold transition-all transform hover:scale-[1.02] text-white"
              style={{
                background: `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`,
                boxShadow: `0 10px 15px -3px ${branding.primaryColor}30, 0 4px 6px -2px ${branding.primaryColor}20`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(to right, ${branding.primaryColor}dd, ${branding.secondaryColor}dd)`;
                e.currentTarget.style.boxShadow = `0 20px 25px -5px ${branding.primaryColor}40, 0 10px 10px -5px ${branding.primaryColor}30`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`;
                e.currentTarget.style.boxShadow = `0 10px 15px -3px ${branding.primaryColor}30, 0 4px 6px -2px ${branding.primaryColor}20`;
              }}
            >
              {isValidating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Signing in...
                </>
              ) : (
                <>
                  {showMasterKey ? <Code className="h-4 w-4 mr-2" /> : <Key className="h-4 w-4 mr-2" />}
                  Sign In
                </>
              )}
            </Button>
          </form>

          {/* SSO Login Options */}
          {!showMasterKey && (
            <div className="mt-4 sm:mt-5">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white/80 text-slate-500">Or continue with</span>
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-3">
                {/* Azure AD SSO */}
                {(typeof window === 'undefined' || (window as any).RUNTIME_CONFIG?.AZURE_AD_OAUTH_ENABLED !== false) && (
                <Button
                  type="button"
                  onClick={() => {
                    const apiBaseUrl = getAPIBaseUrl();
                    const origin = encodeURIComponent(window.location.origin);
                    window.location.href = `${apiBaseUrl}/auth/azure/login?origin=${origin}`;
                  }}
                  className="w-full h-11 text-sm font-medium bg-white border border-slate-300 text-black hover:bg-slate-50 hover:border-slate-400 shadow-sm transition-all duration-200 flex items-center justify-center gap-2 group"
                >
                  {/* Official Microsoft Logo */}
                  <svg 
                    className="h-5 w-5 transition-transform group-hover:scale-110" 
                    viewBox="0 0 23 23" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M0 0H10.5V10.5H0V0Z" fill="#F25022"/>
                    <path d="M12.5 0H23V10.5H12.5V0Z" fill="#7FBA00"/>
                    <path d="M0 12.5H10.5V23H0V12.5Z" fill="#00A4EF"/>
                    <path d="M12.5 12.5H23V23H12.5V12.5Z" fill="#FFB900"/>
                  </svg>
                  <span className="font-semibold text-black">Microsoft</span>
                </Button>
                )}
                
                {/* Google SSO */}
                {(typeof window === 'undefined' || (window as any).RUNTIME_CONFIG?.GOOGLE_OAUTH_ENABLED !== false) && (
                <Button
                  type="button"
                  onClick={() => {
                    const apiBaseUrl = getAPIBaseUrl();
                    const origin = encodeURIComponent(window.location.origin);
                    window.location.href = `${apiBaseUrl}/auth/google/login?origin=${origin}`;
                  }}
                  className="w-full h-11 text-sm font-medium bg-white border border-slate-300 text-black hover:bg-slate-50 hover:border-slate-400 shadow-sm transition-all duration-200 flex items-center justify-center gap-2 group"
                >
                  <svg 
                    className="h-5 w-5 transition-transform group-hover:scale-110" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="font-semibold text-black">Google</span>
                </Button>
                )}
              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-slate-200">
            {/* <p className="text-xs text-center text-slate-500">
              Don't have an account?{' '}
              <button
                type="button"
                className="font-medium transition-colors"
                style={{ color: branding.primaryColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = branding.secondaryColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = branding.primaryColor;
                }}
                onClick={() => setShowRegister(true)}
              >
                Sign up
              </button>
            </p> */}
            <p className="text-xs text-center text-slate-500 mt-2">
              Need help? Contact your{' '}
              <button
                type="button"
                onClick={() => setShowHelpModal(true)}
                className="font-medium transition-colors"
                style={{ color: branding.primaryColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = branding.secondaryColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = branding.primaryColor;
                }}
              >
                administrator
              </button>
            </p>
          </div>
        </div>

        {/* Three cards below login */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center shrink-0">
          {snapshotItems.map((item, i) => (
            <div 
              key={i} 
              className="p-2.5 sm:p-3 bg-white/60 backdrop-blur-sm rounded-xl border border-slate-200/50"
            >
              <div 
                className="text-lg sm:text-xl lg:text-2xl font-bold bg-clip-text text-transparent leading-tight"
                style={{
                  background: `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {item.value}
              </div>
              <div className="text-xs text-slate-600 font-medium mt-1">{item.label}</div>
            </div>
          ))}
        </div>
        </div>
      </div>
    </div>

    {canUseDOM && showHelpModal && createPortal(
      <Modal
        isOpen={showHelpModal}
        onClose={() => {
          if (!isSendingHelp) {
            setShowHelpModal(false);
            setHelpSuccessSent(false);
            setHelpSubject('');
            setHelpMessage('');
            setHelpMessageTouched(false);
          }
        }}
        title="Contact administrator"
        size="lg"
      >
        <div className="space-y-6">
          {helpSuccessSent ? (
            <div className="py-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600 mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-semibold text-green-800">Message sent successfully</p>
              <p className="text-sm text-slate-600 mt-1">
                Your message has been sent to the administrators.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Send a message to your administrator. You do not need to be logged in.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Subject (optional)
                  </label>
                  <input
                    type="text"
                    value={helpSubject}
                    onChange={(e) => setHelpSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none bg-white text-slate-900 text-sm"
                    placeholder="Short summary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={helpMessage}
                    onChange={(e) => setHelpMessage(e.target.value)}
                    onBlur={() => setHelpMessageTouched(true)}
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none bg-white text-slate-900 text-sm resize-none"
                    placeholder="Describe how we can help..."
                  />
                  {helpMessageError && (
                    <p className="mt-1 text-xs text-red-600">Please enter a message.</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => !isSendingHelp && setShowHelpModal(false)}
                  disabled={isSendingHelp}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleHelpSubmit}
                  isLoading={isSendingHelp}
                  disabled={isSendingHelp || helpMessageError}
                >
                  {isSendingHelp ? 'Sending...' : 'Send message'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>,
      document.body
    )}
    </>
  );
}
